import { useSearchParams } from 'react-router-dom'

import { ResourceHeader } from '@factorialco/f0-react/dist/experimental'
import { RateConfigurationFormFragment } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { getCurrencySymbolFromCode } from 'modules/core/lib/formatCurrencyUtils'
import i18n from 'modules/core/lib/i18n'

import {
  ExpensableRateConfigurationResourceType,
  ExpensableRateConfigurationResourceTypeEnum,
} from 'modules/expenses/types/Expensable'
import { amountWithCurrency } from 'modules/finance/lib/budgets'

interface RateHeaderProps {
  rate?: RateConfigurationFormFragment
  type: ExpensableRateConfigurationResourceType
}
export const RateHeader = ({ rate, type }: RateHeaderProps) => {
  const { access } = useCurrentSession()
  const locale = access.locale
  const strategy = rate?.strategy
  const [_searchParams, setSearchParams] = useSearchParams()

  const setShowAssigneesModal = () => setSearchParams({ modal: 'assignees' })

  if (!rate) {
    return null
  }
  const renderRateOrAmount = (rateConfiguration: RateConfigurationFormFragment): string => {
    if (type === ExpensableRateConfigurationResourceTypeEnum.PerDiem) {
      return String(
        amountWithCurrency(
          Number(rateConfiguration.amountCents),
          rateConfiguration.currency,
          locale
        )
      )
    } else {
      return (
        rateConfiguration?.currency &&
        `${rateConfiguration?.rate} ${getCurrencySymbolFromCode(rateConfiguration.currency)}`
      )
    }
  }

  const metadata = [
    ...(rate.legalEntityIds.length > 0
      ? [
          {
            label:
              rate.legalEntityIds.length === 1
                ? i18n.t('expenses_v2.settings.rate_configurations.detail.fields.legal_entity')
                : i18n.t('expenses_v2.settings.rate_configurations.detail.fields.legal_entities'),
            value: {
              data: rate.legalEntitiesConnection.nodes.map((le) => le.legalName),
              type: 'data-list' as const,
            },
          },
        ]
      : []),
    {
      label:
        type === ExpensableRateConfigurationResourceTypeEnum.PerDiem
          ? i18n.t('expenses_v2.settings.rate_configurations.detail.fields.amount')
          : i18n.t('expenses_v2.settings.rate_configurations.detail.fields.rate'),
      value: {
        content: renderRateOrAmount(rate),
        type: 'text' as const,
      },
    },
  ]

  return (
    <ResourceHeader
      title={rate.name}
      metadata={metadata}
      primaryAction={
        strategy === 'manual_selection'
          ? {
              label: i18n.t('expenses_v2.settings.rate_configurations.detail.assignees_modal.cta'),
              onClick: () => setShowAssigneesModal(),
            }
          : undefined
      }
    />
  )
}
